import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import moment from 'moment-timezone';

import Strings from '../../../Strings';
import { getProgramDisplayName } from '../../../utils/cmsPrograms';
import { extractErrorMessage, extractExceptionMessage, formatTimestamp, getGenerationSummaryMessage, INVOICE_STATUSES, normalizeInvoiceComment, parseDate, parseDateYMD, parseMoney, validateInvoice } from './helpers';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { GET_DEVICE_INVOICE_INITIAL_DETAILS_RESULT, GET_DEVICE_INVOICE_INITIAL_HISTORY_RESULT, GET_DEVICE_INVOICE_RECURRING_DETAILS_RESULT, GET_DEVICE_INVOICE_RECURRING_HISTORY_RESULT, invoiceActions, RECALCULATE_DEVICE_INVOICE_INITIAL_RESULT, RECALCULATE_DEVICE_INVOICE_RECURRING_RESULT, UPDATE_DEVICE_INVOICE_INITIAL_ERROR, UPDATE_DEVICE_INVOICE_RECURRING_ERROR } from './redux/actions';
import { PageHeader } from '../../../components/PageHeader';
import { DatePickerField, Input, SelectField, TextArea } from '../../../containers/Form';
import { DATE_MONTH_DAY_YEAR } from '../../../constants';
import { ORGANIZATION_LIST_RESULT } from '../../Organizations/redux/constants';
import { objectToTooltip } from '../../../utils';
import { getTooltipArrayFromValidationErrors } from '../../../modals/_ModalsMetadata/ModalsMetadata';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { openModalAction } from '../../../actions/modal';
import { isSuperUser, isShipper } from '../../../utils/userRoles';
import { actions as organizationActions } from '../../Organizations/redux/actions';

const parseHistoryPayload = payload => {
  if (!payload) {
    return null;
  }
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch (e) {
      return null;
    }
  }
  return payload;
};

const getHistoryChanges = entry => {
  const payload = parseHistoryPayload(entry?.payload);
  return Array.isArray(payload?.changes) ? payload.changes : [];
};

function InvoiceDetails(props) {
  const { location, match, showNotification } = props;
  const { getDeviceInvoiceInitialDetails, getDeviceInvoiceRecurringDetails, getDeviceInvoiceInitialHistory, getDeviceInvoiceRecurringHistory } = props;
  const { updateDeviceInvoiceInitial, updateDeviceInvoiceRecurring, recalculateDeviceInvoiceInitial, recalculateDeviceInvoiceRecurring, openConfirmModal } = props;
  const { getOrgs } = props;

  const invoiceId = React.useMemo(() => match.params.id, [match.params.id]);
  const type = React.useMemo(() => match.params.type, [match.params.type]);
  const stateData = location?.state;
  const { invoice } = stateData || {};
  const toEditableInvoiceData = React.useCallback((details = {}) => ({
    ...details,
    invoice_date: details.invoice_date ? moment(details.invoice_date) : null,
    shipment_date_from_rxcap: details.shipment_date_from_rxcap ? moment(details.shipment_date_from_rxcap) : null,
    billing_end_date: details.billing_end_date ? moment(details.billing_end_date) : null,
    comment: details.comment || '',
  }), []);

  const [invoiceData, setInvoiceData] = React.useState(invoice ? toEditableInvoiceData(invoice) : {});
  const [originalInvoiceData, setOriginalInvoiceData] = React.useState(invoice ? toEditableInvoiceData(invoice) : {});
  const [invoiceHistory, setInvoiceHistory] = React.useState([]);
  const [relatedItems, setRelatedItems] = React.useState([]);
  const [careProviders, setCareProviders] = React.useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);

  const isRecurring = type === 'recurring';
  const canManageDeviceInvoices = isSuperUser() || isShipper();
  const displayInvoice = invoiceData?.invoice_uuid === invoiceId ? invoiceData : invoice;

  React.useEffect(() => {
    setInvoiceData(invoice ? toEditableInvoiceData(invoice) : {});
    setOriginalInvoiceData(invoice ? toEditableInvoiceData(invoice) : {});
    setInvoiceHistory([]);
    setRelatedItems([]);
  }, [invoiceId, type, invoice, toEditableInvoiceData]);

  React.useEffect(() => {
    getOrgs('care-provider').then(resp => {
      if (resp?.type === ORGANIZATION_LIST_RESULT) {
        setCareProviders(
          resp.response?.data?.map(provider => ({
            ...provider,
            value: provider.masked_id,
            label: provider.name,
          })) || [],
        );
      }
    });
  }, [getOrgs]);

  const getInvoiceDetailsAndHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const detailsAction = isRecurring ? getDeviceInvoiceRecurringDetails : getDeviceInvoiceInitialDetails;
      const detailsResp = await detailsAction(invoiceId);
      if (detailsResp.type === GET_DEVICE_INVOICE_RECURRING_DETAILS_RESULT || detailsResp.type === GET_DEVICE_INVOICE_INITIAL_DETAILS_RESULT) {
        setInvoiceData(toEditableInvoiceData(detailsResp.response));
        setOriginalInvoiceData(toEditableInvoiceData(detailsResp.response));
      } else {
        showNotification(Strings.errorLoadingInvoiceDetails, 5000, true);
      }

      const action = isRecurring ? getDeviceInvoiceRecurringHistory : getDeviceInvoiceInitialHistory;
      const resp = await action(invoiceId);
      if (resp.type === GET_DEVICE_INVOICE_RECURRING_HISTORY_RESULT || resp.type === GET_DEVICE_INVOICE_INITIAL_HISTORY_RESULT) {
        setInvoiceHistory(resp.response.entries);
        setRelatedItems(resp.response.related_items);
      } else {
        showNotification(Strings.errorLoadingHistory, 5000, true);
      }
    } catch (error) {
      showNotification(Strings.errorLoadingHistory, 5000, true);
    }
    setIsLoadingHistory(false);
  };

  React.useEffect(() => {
    if (invoiceId) {
      getInvoiceDetailsAndHistory();
    }
  }, [invoiceId, isRecurring, getDeviceInvoiceInitialHistory, getDeviceInvoiceRecurringHistory, showNotification]);

  React.useEffect(() => {
    setValidationErrors(validateInvoice(invoiceData, true, isRecurring));
  }, [invoiceData, originalInvoiceData?.billing_status, isRecurring]);

  const canRecalculate = [INVOICE_STATUSES.action_required.key, INVOICE_STATUSES.ready_for_approval.key].includes(invoiceData?.billing_status);

  const handleRecalculate = React.useCallback(() => {
    const recalculateAction = isRecurring ? recalculateDeviceInvoiceRecurring : recalculateDeviceInvoiceInitial;
    const payload = { invoice_ids: [invoiceData?.invoice_uuid] };

    openConfirmModal({
      title: Strings.deviceInvoices.recalculateInvoicesWarning,
      batchWarning: Strings.deviceInvoices.batchActionSingleWarning,
      onConfirmAction: () => recalculateAction(payload).then(resp => {
        const expectedType = isRecurring ? RECALCULATE_DEVICE_INVOICE_RECURRING_RESULT : RECALCULATE_DEVICE_INVOICE_INITIAL_RESULT;
        if (resp.type === expectedType) {
          showNotification(getGenerationSummaryMessage(resp.response), 5000, false);
        }
        return resp;
      }),
      confirmPostAction: getInvoiceDetailsAndHistory,
      caption: Strings.deviceInvoices.recalculateInvoice,
      hideCaution: true,
      onCancelAction: null,
    });
  }, [getInvoiceDetailsAndHistory, invoiceData?.invoice_uuid, isRecurring, openConfirmModal, recalculateDeviceInvoiceInitial, recalculateDeviceInvoiceRecurring, showNotification]);

  const onSubmit = async () => {
    setIsSaving(true);

    let payload;
    const price = parseMoney(invoiceData?.price);

    if (isRecurring) {
      const billingEndDate = parseDateYMD(invoiceData?.billing_end_date);
      const dataFee = parseMoney(invoiceData?.data_fee);

      payload = {
        price,
        data_fee: dataFee,
        provider_id: invoiceData.provider_id || undefined,
        clear_provider_id: Boolean(originalInvoiceData?.provider_id && !invoiceData?.provider_id),
        enrollment_id: invoiceData.enrollment_id || undefined,
        clear_enrollment_id: Boolean(originalInvoiceData?.enrollment_id && !invoiceData?.enrollment_id),
        billing_end_date: billingEndDate,
        clear_billing_end_date: Boolean(originalInvoiceData?.billing_end_date && !invoiceData?.billing_end_date),
        comment: normalizeInvoiceComment(invoiceData?.comment),
        status: invoiceData?.billing_status !== originalInvoiceData?.billing_status ? invoiceData?.billing_status : undefined
      };
    } else {
      const kittingFee = parseMoney(invoiceData.kitting_fee);
      const shippingFee = parseMoney(invoiceData.shipping_fee);
      const invoiceDate = parseDateYMD(invoiceData.invoice_date);
      const originalShipmentDateFromRxcap = parseDate(originalInvoiceData?.shipment_date_from_rxcap);
      const shipmentDateFromRxcap = parseDate(invoiceData.shipment_date_from_rxcap);
      const shipmentDateFromRxcapChanged = shipmentDateFromRxcap !== originalShipmentDateFromRxcap;
      payload = {
        price,
        kitting_fee: kittingFee,
        shipping_fee: shippingFee,
        provider_id: invoiceData.provider_id || undefined,
        clear_provider_id: Boolean(originalInvoiceData?.provider_id && !invoiceData?.provider_id),
        invoice_date: invoiceDate,
        clear_invoice_date: Boolean(originalInvoiceData?.invoice_date && !invoiceData?.invoice_date),
        ...(shipmentDateFromRxcapChanged ? { shipment_date_from_rxcap: shipmentDateFromRxcap } : {}),
        clear_shipment_date_from_rxcap: shipmentDateFromRxcapChanged && Boolean(originalInvoiceData?.shipment_date_from_rxcap && !invoiceData?.shipment_date_from_rxcap),
        comment: normalizeInvoiceComment(invoiceData?.comment),
        status: invoiceData?.billing_status !== originalInvoiceData?.billing_status ? invoiceData?.billing_status : undefined,
      };
    }

    const action = isRecurring ? updateDeviceInvoiceRecurring : updateDeviceInvoiceInitial;
    action(invoiceData.invoice_uuid, payload)
      .then(resp => {
        if (resp?.type === UPDATE_DEVICE_INVOICE_INITIAL_ERROR || resp?.type === UPDATE_DEVICE_INVOICE_RECURRING_ERROR) {
          showNotification(extractErrorMessage(resp), 5000, true);
          setIsSaving(false);
          return;
        }
        showNotification(Strings.deviceInvoices.invoiceUpdated);
        getInvoiceDetailsAndHistory();
        setIsSaving(false);
      })
      .catch(error => {
        showNotification(extractExceptionMessage(error), 5000, true);
        setIsSaving(false);
      });
  };

  return (
    <>
      <PageHeader
        onBackClick={() => history.back()}
        left={`${Strings.deviceInvoices.deviceInvoice} | ${displayInvoice?.customer_name || ''}`}
        right={canManageDeviceInvoices ? (
          <>
            {canRecalculate ? (
              <div className="button-container">
                <Button
                  onClick={handleRecalculate}
                  class={HEADER_BUTTON_DARK_BLUE}
                  title={Strings.deviceInvoices.recalculateInvoice || 'Recalculate Invoice'}
                  disabled={isSaving}
                />
              </div>
            ) : null}
            <div
              className="button-container"
              data-tooltip-html={objectToTooltip(getTooltipArrayFromValidationErrors(validationErrors))}
              data-tooltip-id={validationErrors.length > 0 ? "tooltip" : undefined}
            >
              <Button
                onClick={() => onSubmit()}
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.saveChanges || 'Save Changes'}
                disabled={isSaving || validationErrors.length > 0}
              />
            </div>
          </>
        ) : null}
      />
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        className="scrollbar-right-margin config-tab-container"
      >
        <div className="rjsf">
          <fieldset>
            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.deviceInvoices.invoiceDetails || 'Invoice Details'}</header>
                <Input
                  name="invoice_uuid"
                  label={Strings.deviceInvoices.invoiceUuid || "Invoice UUID"}
                  value={invoiceData?.invoice_uuid}
                  disabled
                />
                <SelectField
                  name="billing_status"
                  label={Strings.status}
                  value={invoiceData?.billing_status}
                  onChange={option => {
                    setInvoiceData({ ...invoiceData, billing_status: option.value });
                  }}
                  data={Object.values(INVOICE_STATUSES).map(v => ({ value: v.key, label: v.name }))}
                  highlightInvalid
                  disabled={!canManageDeviceInvoices}
                />
                <SelectField
                  name="provider"
                  label={Strings.capPatient.provider || 'Provider'}
                  value={invoiceData?.provider_id}
                  onChange={option => {
                    setInvoiceData({ ...invoiceData, provider_id: option?.value || null });
                  }}
                  data={careProviders}
                  isSearchable
                  isClearable
                  highlightInvalid
                  disabled={!canManageDeviceInvoices}
                />
                {!isRecurring ? (
                  <>
                    <DatePickerField
                      name="invoice_date"
                      label={Strings.deviceInvoices.invoiceDate || "Invoice Date"}
                      value={invoiceData?.invoice_date}
                      onChange={date => setInvoiceData({ ...invoiceData, invoice_date: date })}
                      highlightInvalid
                      disabled={!canManageDeviceInvoices}
                    />
                    <Input
                      name="kitting_fee"
                      type="number"
                      label={Strings.deviceInvoices.kittingFee}
                      value={isNaN(invoiceData?.kitting_fee) ? '' : invoiceData?.kitting_fee}
                      onChange={e => setInvoiceData({ ...invoiceData, kitting_fee: e.target.value })}
                      isRequired
                      highlightInvalid
                      disabled={!canManageDeviceInvoices}
                    />
                    <Input
                      name="shipping_cost"
                      type="number"
                      label={Strings.shipping.shippingCost || "Shipping Cost"}
                      value={isNaN(invoiceData?.shipping_fee) ? '' : invoiceData?.shipping_fee}
                      onChange={e => setInvoiceData({ ...invoiceData, shipping_fee: e.target.value })}
                      isRequired
                      highlightInvalid
                      disabled={!canManageDeviceInvoices}
                    />
                    <DatePickerField
                      name="shipment_date_from_rxcap"
                      label={Strings.shipmentDate || "Shipment Date"}
                      value={invoiceData?.shipment_date_from_rxcap}
                      onChange={date => setInvoiceData({ ...invoiceData, shipment_date_from_rxcap: date })}
                      highlightInvalid
                      disabled={!canManageDeviceInvoices}
                    />
                  </>
                ) : (
                  <>
                    <Input
                      name="enrollment_id"
                      label={Strings.enrollmentId || "Enrollment ID"}
                      value={invoiceData?.enrollment_id}
                      onChange={e => setInvoiceData({ ...invoiceData, enrollment_id: e.target.value })}
                      disabled={!canManageDeviceInvoices}
                    />
                    <DatePickerField
                      name="billing_end_date"
                      label={Strings.deviceInvoices.billingEndDate || "Billing End Date"}
                      value={invoiceData?.billing_end_date}
                      onChange={date => setInvoiceData({ ...invoiceData, billing_end_date: date })}
                      highlightInvalid
                      disabled={!canManageDeviceInvoices}
                    />
                  </>
                )}
                {isRecurring && (
                  <Input
                    name="data_fee"
                    type="number"
                    label={Strings.deviceInvoices.dataFee}
                    value={isNaN(invoiceData?.data_fee) ? '' : invoiceData?.data_fee}
                    onChange={e => setInvoiceData({ ...invoiceData, data_fee: e.target.value })}
                    isRequired
                    highlightInvalid
                    disabled={!canManageDeviceInvoices}
                  />
                )}
                <Input
                  name="price"
                  label={Strings.deviceInvoices.price}
                  value={invoiceData?.price}
                  onChange={e => setInvoiceData({ ...invoiceData, price: e.target.value })}
                  isRequired
                  highlightInvalid
                  disabled={!canManageDeviceInvoices}
                />
                {canManageDeviceInvoices && (
                  <TextArea
                    name="comment"
                    label={Strings.addComment}
                    value={invoiceData?.comment}
                    onChange={e => setInvoiceData({ ...invoiceData, comment: e.target.value })}
                    highlightInvalid
                    isRequired
                  />
                )}
              </fieldset>
            </div>

            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.deviceInvoices.itemDescription}</header>
                {isRecurring ? (
                  <>
                    <Input
                      label={Strings.kitId || "Kit ID"}
                      value={displayInvoice?.kit_id}
                      disabled
                    />
                    <Input
                      label={Strings.enrollmentId || "Enrollment ID"}
                      value={displayInvoice?.enrollment_id}
                      disabled
                    />
                    <Input
                      label={Strings.program || "Program"}
                      value={getProgramDisplayName(displayInvoice?.program, displayInvoice?.subprogram)}
                      disabled
                    />
                    <Input
                      label={Strings.sourceDeviceId || "Source Device ID"}
                      value={displayInvoice?.source_device_id}
                      disabled
                    />
                    <Input
                      label={Strings.billingMonth || "Billing Month"}
                      value={formatTimestamp(displayInvoice?.billing_month, 'MMMM YYYY')}
                      disabled
                    />
                  </>
                ) : (
                  <>
                    <Input
                      label={Strings.deviceId || "Device ID"}
                      value={displayInvoice?.device_id}
                      disabled
                    />
                    <Input
                      label={Strings.deviceManufacturer || "Device Manufacturer"}
                      value={displayInvoice?.device_manufacturer}
                      disabled
                    />
                    <Input
                      label={Strings.deviceModel || "Device Model"}
                      value={displayInvoice?.device_model}
                      disabled
                    />
                    <Input
                      label={Strings.deviceType || "Device Type"}
                      value={displayInvoice?.device_type}
                      disabled
                    />
                  </>
                )}
              </fieldset>
            </div>
            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.issues || 'Issues'}</header>
                {invoiceData?.issues?.map((issue, index) => (
                  <div key={index} className="form-group field field-object">
                    <fieldset style={{display: 'flex'}}>
                      {Strings.deviceInvoices.invoiceIssuesEnum[issue] || issue}
                    </fieldset>
                  </div>
                ))}
              </fieldset>
            </div>
            <div className="form-group field field-object">
              <fieldset>
                <header>{Strings.history || 'History'}</header>
                {invoiceHistory.map(entry => {
                  const historyChanges = getHistoryChanges(entry);
                  return (
                  <div key={entry.id} className="form-group field field-object">
                    <fieldset style={{display: 'flex'}}>
                      <header className="header-with-button">
                        {Strings.deviceInvoices.invoiceHistoryEntryTypes[entry.action_type]}
                        <div className='subheader'>
                          {`${moment(entry.created_at).format(DATE_MONTH_DAY_YEAR)}, ${entry?.created_by_username || "System"}`}
                        </div>
                      </header>
                      {entry.comment ? (
                        <>
                          <div className='subheader'>{Strings.comment || 'Comment'}</div>
                          <div>{entry.comment}</div>
                        </>
                      ) : null}
                      {historyChanges.length > 0 ? (
                        <>
                          <div className='subheader'>{Strings.changes || 'Changes'}</div>
                          {historyChanges.map(change => (
                            <div key={`${entry.id}-${change.field}`}>
                              {`${change.label}: ${change.before} → ${change.after}`}
                            </div>
                          ))}
                        </>
                      ) : null}
                    </fieldset>
                  </div>
                  );
                })}
              </fieldset>
            </div>
            <div className="form-group field field-object">
              <fieldset>
                <header>{Strings.relatedItems || 'Related Items'}</header>
                {relatedItems.map(entry => (
                  <Link
                    key={entry.invoice_uuid}
                    to={{
                      pathname: `/device-invoices/invoice-details/${entry.invoice_type}/${entry.invoice_uuid}`,
                      state: { invoice: entry },
                    }}
                    style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
                  >
                    <div className="form-group field field-object">
                      <fieldset style={{ display: 'flex', cursor: 'pointer' }}>
                        <header>{entry.title} - {Strings.deviceInvoices.relationTypes[entry.relation_type]}</header>
                        <div>{entry.invoice_type === 'initial' ? Strings.deviceInvoices.initial : Strings.deviceInvoices.recurring}</div>
                        <div>{INVOICE_STATUSES[entry.billing_status]?.name || entry.billing_status}</div>
                        <div>{entry?.subtitle}</div>
                      </fieldset>
                    </div>
                  </Link>
                ))}
              </fieldset>
            </div>
          </fieldset>
        </div>
      </OverlayScrollbarsComponent>
    </>
  );
}

InvoiceDetails.propTypes = {
};

const mapDispatchToProps = dispatch => ({
  getDeviceInvoiceInitialDetails: invoiceUuid => dispatch(invoiceActions.getDeviceInvoiceInitialDetails(invoiceUuid)),
  getDeviceInvoiceRecurringDetails: invoiceUuid => dispatch(invoiceActions.getDeviceInvoiceRecurringDetails(invoiceUuid)),
  getDeviceInvoiceInitialHistory: params => dispatch(invoiceActions.getDeviceInvoiceInitialHistory(params)),
  getDeviceInvoiceRecurringHistory: params => dispatch(invoiceActions.getDeviceInvoiceRecurringHistory(params)),
  updateDeviceInvoiceInitial: (invoiceUuid, data) => dispatch(invoiceActions.updateDeviceInvoiceInitial(invoiceUuid, data)),
  updateDeviceInvoiceRecurring: (invoiceUuid, data) => dispatch(invoiceActions.updateDeviceInvoiceRecurring(invoiceUuid, data)),
  recalculateDeviceInvoiceInitial: payload => dispatch(invoiceActions.recalculateDeviceInvoiceInitial(payload)),
  recalculateDeviceInvoiceRecurring: payload => dispatch(invoiceActions.recalculateDeviceInvoiceRecurring(payload)),
  updateDeviceInvoiceInitialComment: payload => dispatch(invoiceActions.updateDeviceInvoiceInitialComment(payload)),
  updateDeviceInvoiceRecurringComment: payload => dispatch(invoiceActions.updateDeviceInvoiceRecurringComment(payload)),
  deleteDeviceInvoiceInitialComment: payload => dispatch(invoiceActions.deleteDeviceInvoiceInitialComment(payload)),
  deleteDeviceInvoiceRecurringComment: payload => dispatch(invoiceActions.deleteDeviceInvoiceRecurringComment(payload)),
  getOrgs: request => dispatch(organizationActions.getOrgs(request)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(InvoiceDetails);
